import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MapPin, X, Instagram, Facebook, ChevronDown } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DesktopNav, MobileMenu, HeaderActions } from './HeaderComponents';
import { createPortal } from 'react-dom';
import { SmartSearch } from './SmartSearch';
import { logger } from '../utils/logger';
import { openCookieSettings } from '../lib/consent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompactHeader = ({ isFixed = false, scrolled, searchOpen, setSearchOpen, mobileMenuOpen, setMobileMenuOpen }) => (
  <header
    className={`${isFixed ? 'fixed' : 'sticky'} top-0 z-50 bg-[#000000] border-b border-[#222222] transition-transform duration-300 w-full ${isFixed && !scrolled ? '-translate-y-full' : 'translate-y-0'}`}
    data-testid="compact-header"
  >
    <div className="px-4 md:px-8 lg:px-16">
      <div className="flex items-center justify-between h-14">
        <Link to="/" className="flex items-center" data-testid="header-logo-compact" aria-label="Split Soul Active — Home" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <span className="text-[#DEA193] text-[10px] sm:text-xs tracking-[0.2em] leading-tight">
            <span className="hidden sm:inline">Move. Express. Empower.</span>
            <span className="sm:hidden">Split Soul Active</span>
          </span>
        </Link>
        <DesktopNav compact />
        <HeaderActions compact searchOpen={searchOpen} setSearchOpen={setSearchOpen} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      </div>
      <MobileMenu open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </div>
  </header>
);

const AnnouncementBar = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const res = await axios.get(`${API}/announcement`);
        if (res.data.enabled) setAnnouncement(res.data);
      } catch (error) {
        logger.error('Failed to fetch announcement:', error);
      }
    };
    fetchAnnouncement();
  }, []);

  if (!announcement || dismissed) return null;

  const isSale = announcement.style === 'sale';
  const bgColor = announcement.bg_color || (isSale ? '#000000' : '#DEA193');
  const textColor = isSale ? '#FFFFFF' : '#000000';
  const accentColor = isSale ? '#DEA193' : '#000000';

  return (
    <div className="text-center relative" style={{ backgroundColor: bgColor }} data-testid="announcement-bar">
      <div className="px-8 py-2.5 flex items-center justify-center gap-3">
        {isSale ? (
          <span className="text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 border rounded-sm" style={{ color: accentColor, borderColor: accentColor }}>SALE</span>
        ) : (
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: textColor }} />
        )}
        <p className="text-xs sm:text-sm tracking-wide" style={{ color: textColor }}>
          {announcement.text}
          {announcement.link_url && (
            <a href={announcement.link_url} className="ml-2 underline underline-offset-2 font-medium transition-colors hover:opacity-70" style={{ color: isSale ? accentColor : textColor }}>
              {announcement.link_text || 'Shop Now'}
            </a>
          )}
        </p>
        <button onClick={() => setDismissed(true)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 hover:opacity-70 rounded-sm" data-testid="announcement-dismiss" aria-label="Dismiss announcement">
          <X className="w-3.5 h-3.5" style={{ color: textColor }} />
        </button>
      </div>
    </div>
  );
};

export const Header = ({ compact = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isProductPage = location.pathname.startsWith('/product/');
  const isHomePage = location.pathname === '/';
  const showHomeHeader = !compact && isHomePage && !isProductPage;

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 100);
  }, []);

  useEffect(() => {
    if (showHomeHeader) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    }
    setScrolled(false);
  }, [showHomeHeader, handleScroll]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const compactProps = { scrolled, searchOpen, setSearchOpen, mobileMenuOpen, setMobileMenuOpen };

  // Non-homepage: just the compact header (sticky)
  if (!showHomeHeader) {
    return (
      <>
        <AnnouncementBar />
        <CompactHeader {...compactProps} />
        {searchOpen && createPortal(<SmartSearch onClose={() => setSearchOpen(false)} />, document.body)}
      </>
    );
  }

  // HOMEPAGE: Large logo scrolls naturally, compact header slides in from top when scrolled
  return (
    <>
      <AnnouncementBar />
      {/* Large logo header — scrolls with page (not sticky) */}
      <header className="relative z-50 bg-[#000000] border-b border-[#222222]" data-testid="home-header">
        <div className="px-4 md:px-8 lg:px-16">
          <div className="flex items-center justify-center py-4">
            <Link to="/" className="flex items-center justify-center" data-testid="header-logo" aria-label="Split Soul Active — Home">
              <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_dancekit-hub/artifacts/xsbrt1bw_Image%20%2810%29.jpg" alt="Split Soul Active" className="w-full max-w-[280px] md:max-w-[420px] h-auto object-contain mix-blend-lighten" />
            </Link>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-[#222222]">
            <DesktopNav />
            <HeaderActions searchOpen={searchOpen} setSearchOpen={setSearchOpen} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
          </div>
          <MobileMenu open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
        </div>
      </header>
      {/* Compact header — fixed, slides in when user scrolls past the large header */}
      <CompactHeader isFixed {...compactProps} />
      {searchOpen && createPortal(<SmartSearch onClose={() => setSearchOpen(false)} />, document.body)}
    </>
  );
};

export const Footer = () => {
  const [openSection, setOpenSection] = useState('');
  const toggle = (name) => setOpenSection(prev => prev === name ? '' : name);

  const sections = [
    {
      title: 'Shop',
      links: [
        { to: '/collections/dance', label: 'Dance' },
        { to: '/collections/activewear', label: 'Activewear' },
        { to: '/collections/accessories', label: 'Accessories' },
        { to: '/shop?category=Nutrition', label: 'Nutrition' },
        { to: '/shop', label: 'All Brands' },
        { to: '/gift-cards', label: 'Gift Cards' },
      ],
    },
    {
      title: 'Help',
      links: [
        { to: '/delivery', label: 'Delivery' },
        { to: '/returns', label: 'Returns' },
        { to: '/size-guide', label: 'Size Guide' },
        { to: '/faqs', label: 'FAQs' },
        { to: '/track', label: 'Track Order' },
        { href: 'mailto:shout@splitsoul.co.uk', label: 'Contact Us' },
      ],
    },
    {
      title: 'About',
      links: [
        { to: '/our-story', label: 'Our Story' },
        { to: '/events', label: 'Events' },
        { to: '/loyalty', label: 'Rewards' },
        { to: '/referrals', label: 'Refer a Friend' },
      ],
    },
  ];

  return (
    <footer className="bg-[#000000] text-white" data-testid="site-footer">
      <div className="px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Brand */}
          <div className="md:col-span-3">
            <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_dancekit-hub/artifacts/xsbrt1bw_Image%20%2810%29.jpg" alt="Split Soul Active" className="h-16 w-auto object-contain mix-blend-lighten mb-4" />
            <p className="text-[#737373] text-sm leading-relaxed max-w-xs">
              Performance wear that moves with you. Dance. Fitness. You.
            </p>
            <div className="flex items-center gap-4 mt-5">
              <a href="https://www.instagram.com/splitsoulactive/" target="_blank" rel="noopener noreferrer" className="text-[#737373] hover:text-[#DEA193] transition-colors" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/splitsoulactive" target="_blank" rel="noopener noreferrer" className="text-[#737373] hover:text-[#DEA193] transition-colors" data-testid="footer-facebook" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Link Sections — desktop columns, mobile accordions */}
          {sections.map(({ title, links }) => (
            <div key={title} className="md:col-span-3" data-testid={`footer-section-${title.toLowerCase()}`}>
              {/* Desktop heading */}
              <h4 className="hidden md:block text-xs tracking-[0.2em] uppercase mb-5 text-white/90">{title}</h4>
              <ul className="hidden md:block space-y-3">
                {links.map(link => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link to={link.to} className="text-sm text-[#737373] hover:text-white transition-colors" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                        {link.label}
                      </Link>
                    ) : (
                      <a href={link.href} className="text-sm text-[#737373] hover:text-white transition-colors" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}>
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>

              {/* Mobile accordion */}
              <div className="md:hidden border-b border-white/10">
                <button
                  onClick={() => toggle(title)}
                  className="w-full flex items-center justify-between py-4 text-xs tracking-[0.2em] uppercase text-white/90"
                  aria-expanded={openSection === title}
                  data-testid={`footer-toggle-${title.toLowerCase()}`}
                >
                  {title}
                  <ChevronDown className={`w-4 h-4 text-[#737373] transition-transform ${openSection === title ? 'rotate-180' : ''}`} />
                </button>
                {openSection === title && (
                  <ul className="pb-4 space-y-3">
                    {links.map(link => (
                      <li key={link.label}>
                        {link.to ? (
                          <Link to={link.to} className="text-sm text-[#737373] hover:text-white transition-colors">{link.label}</Link>
                        ) : (
                          <a href={link.href} className="text-sm text-[#737373] hover:text-white transition-colors">{link.label}</a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#737373] text-xs">&copy; {new Date().getFullYear()} Split Soul Active. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-[#737373] text-xs hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="text-[#737373] text-xs hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link>
            <button type="button" onClick={openCookieSettings} className="text-[#737373] text-xs hover:text-white transition-colors" data-testid="footer-cookie-settings">Cookie settings</button>
          </div>
        </div>
      </div>
    </footer>
  );
};
